import dayjs, { Dayjs, ManipulateType } from 'dayjs';

export type PickerGridMode = 'date' | 'month' | 'year' | 'decade';

export const PICKER_CELL_ATTRIBUTE = 'data-picker-cell';

const GRID_MODES = new Set<string>(['date', 'month', 'year', 'decade']);

export const isPickerGridMode = (mode: string): mode is PickerGridMode => GRID_MODES.has(mode);

/** The key a grid cell is stamped with, so the keyboard can find the cell for a date. */
export const pickerCellKey = (date: Dayjs, mode: PickerGridMode) => {
  if (mode === 'date') return date.format('YYYY-MM-DD');
  if (mode === 'month') return date.format('YYYY-MM');
  if (mode === 'year') return date.format('YYYY');
  return String(Math.floor(date.year() / 10) * 10);
};

export const parsePickerCellKey = (key: string) => dayjs(key);

const isInSameSpan = (date: Dayjs, view: Dayjs, mode: PickerGridMode) => {
  if (mode === 'date') return date.isSame(view, 'month');
  if (mode === 'month') return date.isSame(view, 'year');
  const span = mode === 'year' ? 10 : 100;
  return Math.floor(date.year() / span) === Math.floor(view.year() / span);
};

/** Moves the keyboard's date into what the panel shows, keeping its finer parts where it can. */
export const keepActiveDateInView = (active: Dayjs, view: Dayjs, mode: PickerGridMode) => {
  if (isInSameSpan(active, view, mode)) return active;
  if (mode === 'date') return view.date(Math.min(active.date(), view.daysInMonth()));
  return active.year(view.year());
};

const CELL_NAME_FORMATS: Partial<Record<PickerGridMode, Intl.DateTimeFormatOptions>> = {
  date: { dateStyle: 'full' },
  month: { month: 'long', year: 'numeric' },
};

const cellNameFormatters = new Map<string, Intl.DateTimeFormat>();

/** A spoken name for a cell whose text alone ("30", "Sep") lacks context. */
export const describePickerCell = (date: Dayjs, mode: PickerGridMode, language: string) => {
  const options = CELL_NAME_FORMATS[mode];
  if (!options) return undefined;
  const cacheKey = `${language}:${mode}`;
  let formatter = cellNameFormatters.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(language, options);
    cellNameFormatters.set(cacheKey, formatter);
  }
  return formatter.format(date.toDate());
};

const PANEL_UNITS: Record<Exclude<PickerGridMode, 'date'>, [number, ManipulateType]> = {
  month: [1, 'month'],
  year: [1, 'year'],
  decade: [10, 'year'],
};

const PANEL_ARROW_OFFSETS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -3,
  ArrowDown: 3,
};

/** Month, year and decade panels lay their cells out three to a row. */
export const getPanelCellTarget = (
  from: Dayjs,
  key: string,
  mode: Exclude<PickerGridMode, 'date'>,
) => {
  const offset = PANEL_ARROW_OFFSETS[key];
  if (offset === undefined) return null;
  const [size, unit] = PANEL_UNITS[mode];
  return from.add(offset * size, unit);
};

/** Home and End go to the first or last cell of the focused cell's row. */
export const getRowEdgeCellKey = (cell: HTMLElement, key: string) => {
  if (key !== 'Home' && key !== 'End') return null;
  const rowCells = cell.closest('tr')?.querySelectorAll<HTMLElement>(`[${PICKER_CELL_ATTRIBUTE}]`);
  if (!rowCells?.length) return null;
  const edge = key === 'Home' ? rowCells[0] : rowCells[rowCells.length - 1];
  return edge.getAttribute(PICKER_CELL_ATTRIBUTE);
};

/** An href-less link (the footer's Today) only takes focus once it has a tabindex. */
export const focusPickerControl = (control: HTMLElement) => {
  if (!control.hasAttribute('tabindex')) control.tabIndex = -1;
  control.focus({ preventScroll: true });
};

const isShown = (element: HTMLElement) =>
  element.style.visibility !== 'hidden' &&
  (typeof element.checkVisibility !== 'function' ||
    element.checkVisibility({ visibilityProperty: true }));

const isUsable = (element: HTMLElement) =>
  !element.matches(':disabled, [aria-disabled="true"]') && isShown(element);

/** Header arrows rendered with a `null` icon are empty, so they are left out along with hidden ones. */
export const getPickerHeaderStops = (popup: HTMLElement) =>
  Array.from(popup.querySelectorAll<HTMLElement>('.ant-picker-header button')).filter(
    (button) => isUsable(button) && (button.childElementCount > 0 || !!button.textContent?.trim()),
  );

export const getPickerFooterStops = (popup: HTMLElement) =>
  Array.from(
    popup.querySelectorAll<HTMLElement>('.ant-picker-footer a, .ant-picker-footer button'),
  ).filter(isUsable);
